import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, UserCheck, Zap } from 'lucide-react';

import { ErrorBanner, InlineSpinner } from '../../components/common/PageState.jsx';
import { adminUsage } from '../../services/api.js';

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function StatCard({ icon: Icon, label, value, accent = 'text-accent' }) {
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-text-secondary">{label}</div>
          <div className="mt-1.5 text-3xl font-semibold text-text-primary tabular-nums">
            {value}
          </div>
        </div>
        <div className={`h-10 w-10 rounded-btn bg-accent/10 flex items-center justify-center ${accent}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function Usage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminUsage();
      setData(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to load usage.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Platform Usage</h1>
        <p className="page-subtitle">
          Aggregate stats across every client organization on ATLACIS Software.
        </p>
      </div>

      {loading && (
        <div className="card p-4">
          <InlineSpinner label="Loading usage…" />
        </div>
      )}
      {error && !loading && (
        <ErrorBanner message={error} detail="admin/usage" onRetry={load} />
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Building2} label="Total Organizations" value={formatNumber(data.total_orgs)} />
            <StatCard icon={UserCheck} label="Active Users" value={formatNumber(data.total_active_users)} />
            <StatCard icon={Users} label="Total Leads" value={formatNumber(data.total_leads)} />
            <StatCard
              icon={Zap}
              label="Credits Used This Month"
              value={formatNumber(data.credits_used_this_month)}
            />
          </div>

          <section className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-border-card">
              <h2 className="section-header mb-0">Top 5 Clients by Usage</h2>
              <p className="text-xs text-text-secondary mt-1">
                Ranked by lifetime enrichment credits consumed.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border-card">
                    <th className="px-5 py-3 font-semibold">#</th>
                    <th className="px-5 py-3 font-semibold">Client</th>
                    <th className="px-5 py-3 font-semibold">Plan</th>
                    <th className="px-5 py-3 font-semibold text-right">Credits Used</th>
                    <th className="px-5 py-3 font-semibold text-right">Remaining</th>
                    <th className="px-5 py-3 font-semibold text-right">Total Allotment</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_orgs_by_usage || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-5 py-10 text-center text-text-secondary"
                      >
                        No credit usage recorded yet.
                      </td>
                    </tr>
                  )}
                  {(data.top_orgs_by_usage || []).map((o, idx) => (
                    <tr
                      key={o.id}
                      className="border-b border-border-card/60 hover:bg-bg-subtle transition-colors"
                    >
                      <td className="px-5 py-3 text-text-secondary tabular-nums">
                        {idx + 1}
                      </td>
                      <td className="px-5 py-3 font-medium">
                        <Link
                          to={`/admin/clients/${o.id}`}
                          className="text-text-primary hover:text-accent"
                        >
                          {o.name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-text-secondary capitalize">
                        {o.plan_tier || '—'}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {formatNumber(o.credits_used)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-accent">
                        {formatNumber(o.credits_remaining)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-text-secondary">
                        {formatNumber(o.credits_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
